import { ButtonText, FlagAvatar, FormField, Heading, Select, TextInput } from '@percent/lemonade'
import { USStates } from '@percent/utility'
import { Organisation } from '@percent/cause-dashboard/services/cause/causeService.types'
import { Alpha3Code } from 'i18n-iso-countries'
import { useTranslation } from 'react-i18next'
import {
  addressFields,
  fieldLabelKey,
  formatDraftAddress,
  isUsaOrganisation,
  OrganisationDetailsDraft,
  OrganisationDetailsErrors
} from './organisationDetails'
import styles from './ReverificationCards.module.scss'

const editableFields = ['website', ...addressFields] as const

const usStateOptions = Object.values(USStates).map(state => ({ label: state, value: state }))

const inputName = (field: keyof OrganisationDetailsDraft) =>
  `organisation${field.charAt(0).toUpperCase()}${field.slice(1)}`

type ReverificationOrganisationCardProps = {
  organisation: Organisation
  value: OrganisationDetailsDraft
  onChange: (value: OrganisationDetailsDraft) => void
  errors: OrganisationDetailsErrors
  isEditing: boolean
  onEditingChange: (isEditing: boolean) => void
}

export function ReverificationOrganisationCard({
  organisation,
  value,
  onChange,
  errors,
  isEditing,
  onEditingChange
}: ReverificationOrganisationCardProps) {
  const { t } = useTranslation()
  const address = formatDraftAddress(value) || organisation.address

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <Heading level="h2" className={styles.sectionTitle}>
          {t('form.reverification.organizationDetails')}
        </Heading>
        <ButtonText type="button" aria-expanded={isEditing} onClick={() => onEditingChange(!isEditing)}>
          {isEditing ? t('button.done') : t('form.reverification.edit')}
        </ButtonText>
      </div>
      <div className={styles.card}>
        <div className={styles.organisationName}>
          <span className={styles.primaryText}>{organisation.name}</span>
          <FlagAvatar code3={organisation.countryCode as Alpha3Code} width="20px" />
        </div>
        {!isEditing && (
          <>
            {value.website && <span className={styles.secondaryText}>{value.website}</span>}
            {address && <span className={styles.secondaryText}>{address}</span>}
          </>
        )}

      {isEditing && (
        <div className={styles.editFields}>
          {editableFields.map(field => (
            <FormField
              key={field}
              label={t(fieldLabelKey(field, organisation))}
              status={errors[field] ? 'danger' : 'default'}
              statusMessage={errors[field]}
            >
              {field === 'state' && isUsaOrganisation(organisation) ? (
                <Select
                  name={inputName(field)}
                  placeholder={t('form.reverification.selectState')}
                  searchable={false}
                  options={usStateOptions}
                  onChange={option => onChange({ ...value, state: option.value })}
                  defaultValue={usStateOptions.find(option => option.value === value.state)}
                />
              ) : (
                <TextInput
                  name={inputName(field)}
                  value={value[field]}
                  onChange={event => onChange({ ...value, [field]: event.target.value })}
                />
              )}
            </FormField>
          ))}
        </div>
      )}
      </div>
    </section>
  )
}
